import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Wrench, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import PresenceStrip from "@/components/PresenceStrip";
import MediaSection from "@/components/MediaSection";
import Devlog from "@/components/Devlog";
import Hero from "@/components/Hero";
import PhaseCard from "@/components/PhaseCard";
import PreLaunchGate from "@/components/PreLaunchGate";
import Seo from "@/components/Seo";

export default function RoadmapPage() {
  const [roadmap, setRoadmap] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/roadmap")
      .then((r) => setRoadmap(r.data))
      .catch(() => setError("FAILED TO LOAD ROADMAP DATA"));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p data-testid="roadmap-error" className="font-mono text-sm tracking-[0.3em] text-red-500 uppercase">{error}</p>
      </div>
    );
  }

  if (!roadmap) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p data-testid="roadmap-loading" className="font-mono text-sm tracking-[0.3em] text-zinc-500 uppercase animate-pulse">
          Loading roadmap data...
        </p>
      </div>
    );
  }

  const allItems = roadmap.phases.flatMap((p) => p.items);
  const overallProgress = allItems.length
    ? Math.round(allItems.reduce((s, i) => s + i.progress, 0) / allItems.length)
    : 0;

  return (
    <div data-testid="roadmap-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo title="New Citizen — V1.0 Release Roadmap" description="New Citizen: a new era of police roleplay in Unreal Engine 5. Follow the V1.0 roadmap from Alpha to Beta — open-world multiplayer life simulation by Specpixel Entertainment." path="/new-citizen" keywords="New Citizen game, police roleplay UE5, New Citizen roadmap, Specpixel game" />
      <SiteNav />
      <PresenceStrip />
      <Hero roadmap={roadmap} overallProgress={overallProgress} />

      {roadmap.announcement && (
        <div data-testid="announcement-strip" className="border-b border-nc-orange/30 bg-nc-orange/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center gap-3">
            <Megaphone className="w-4 h-4 text-nc-orange shrink-0" />
            <p className="font-mono text-[11px] sm:text-xs tracking-[0.2em] text-nc-orange uppercase">
              {roadmap.announcement}
            </p>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20 flex flex-col gap-8 w-full">
        {roadmap.phases.map((phase, i) => (
          <PhaseCard key={phase.id} phase={phase} index={i + 1} />
        ))}

        <MediaSection media={roadmap.media} />

        <Link to="/new-citizen/workshop" data-testid="workshop-banner" className="group border border-nc-blue/40 bg-nc-blue/5 hover:bg-nc-blue/10 p-8 flex flex-col md:flex-row md:items-center gap-6 transition-colors duration-300">
          <span className="w-12 h-12 border border-nc-blue/50 bg-nc-blue/10 flex items-center justify-center shrink-0">
            <Wrench className="w-6 h-6 text-nc-blue" strokeWidth={1.75} />
          </span>
          <div className="flex-1">
            <p className="font-mono text-[9px] tracking-[0.35em] text-nc-blue uppercase">Official // Mod Workshop</p>
            <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mt-2">Mod the city. Share it with everyone.</h2>
            <p className="font-mono text-[11px] text-zinc-400 mt-3 leading-relaxed max-w-2xl">
              The official New Citizen Mod Workshop is live — browse community models, maps, scripts and gameplay mods, download free, and publish your own creations.
            </p>
          </div>
          <span className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2 shrink-0 group-hover:gap-4 transition-all duration-300">
            Open Workshop <ArrowRight className="w-4 h-4" />
          </span>
        </Link>

        <Devlog />

        {/* Partners & Platforms */}
        <section data-testid="partners-section" className="border border-white/10 bg-[#070707] p-8 relative overflow-hidden">
          <div className="absolute inset-0 grid-texture opacity-20" />
          <div className="relative">
            <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase mb-6">Partners & Platforms</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { name: "4NetPlayers", tag: "Official Server Hosting Partner", href: "https://www.4netplayers.com/en/gameserver-hosting/new-citizen/", note: "One-click New Citizen servers" },
                { name: "Graybite", tag: "Web & Platform Partner", href: "https://www.graybite.com/", note: "Studio technology partner" },
                { name: "Xbox Developer Program", tag: "Registered Console Developer", href: null, note: "Dev kit in-studio — console builds in progress" },
                { name: "Steam Partner", tag: "Registered Publisher", href: null, note: "New Citizen sells on Steam at launch" },
              ].map((p, i) => {
                const inner = (
                  <>
                    <p className="font-sans font-bold uppercase text-sm text-zinc-50 group-hover:text-nc-blue transition-colors">{p.name}</p>
                    <p className="font-mono text-[8px] tracking-[0.2em] text-nc-orange uppercase mt-1.5">{p.tag}</p>
                    <p className="font-mono text-[9px] text-zinc-500 mt-2 leading-relaxed">{p.note}</p>
                    {p.href && <span className="font-mono text-[8px] text-nc-blue mt-3 inline-flex items-center gap-1">Visit <ArrowRight className="w-3 h-3" /></span>}
                  </>
                );
                return p.href ? (
                  <a key={i} data-testid={`partner-${i}`} href={p.href} target="_blank" rel="noopener noreferrer" className="group border border-white/10 bg-black/40 p-5 hover:border-nc-blue/40 transition-colors">{inner}</a>
                ) : (
                  <div key={i} data-testid={`partner-${i}`} className="group border border-white/10 bg-black/40 p-5">{inner}</div>
                );
              })}
            </div>
          </div>
        </section>

        <PreLaunchGate gate={roadmap.gate} footer={roadmap.footer} />
      </main>

      <SiteFooter />
    </div>
  );
}
